import type { StakeholderRelationInput } from "../../hooks";
import { parseDelimitedText } from "../../lib/parseDelimitedText";

export interface ParseStakeholderRelationsResult {
  rows: StakeholderRelationInput[];
  /** 行番号（1始まり）付きのエラー。1件でもあれば入稿させない */
  errors: string[];
  /** 同じ組み合わせが複数行にあったために除外した行数 */
  duplicateCount: number;
  /** 同じメールアドレスに複数の氏名が指定されていた人数（先に現れた氏名を採用する） */
  conflictingNameCount: number;
}

const COLUMN_COUNT = 4;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 入稿時と同じ正規化。大文字小文字の違いで別人として扱わないよう揃える */
function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * 関係性の入稿データを解析する。
 *
 * 列は 受け取る人のメール / 受け取る人の氏名 / 回答者のメール / 回答者の氏名 の順。
 * タブ区切り・カンマ区切りのどちらでも受け付ける。
 */
export function parseStakeholderRelations(
  input: string,
): ParseStakeholderRelationsResult {
  const rows: StakeholderRelationInput[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  const nameByEmail = new Map<string, string>();
  const conflictingEmails = new Set<string>();
  let duplicateCount = 0;

  const { records, hasUnterminatedQuote } = parseDelimitedText(input);
  if (hasUnterminatedQuote) {
    errors.push(
      "閉じられていない引用符があります（以降の行が1つの値にまとまっている可能性があります）",
    );
  }

  for (const record of records) {
    const values = record.columns.map((column) => column.trim());
    if (values.every((value) => !value)) continue;

    if (values.length !== COLUMN_COUNT) {
      errors.push(
        `${record.lineNumber}行目: 列数が${values.length}です（4列で指定してください）`,
      );
      continue;
    }

    const [
      rawRecipientEmail,
      recipientName,
      rawRespondentEmail,
      respondentName,
    ] = values;
    const recipientEmail = normalizeEmail(rawRecipientEmail);
    const respondentEmail = normalizeEmail(rawRespondentEmail);

    if (values.some((value) => value.length === 0)) {
      errors.push(`${record.lineNumber}行目: 空欄があります`);
      continue;
    }
    if (!EMAIL_PATTERN.test(recipientEmail)) {
      errors.push(
        `${record.lineNumber}行目: 受け取る人のメールアドレスが不正です（${rawRecipientEmail}）`,
      );
      continue;
    }
    if (!EMAIL_PATTERN.test(respondentEmail)) {
      errors.push(
        `${record.lineNumber}行目: 回答者のメールアドレスが不正です（${rawRespondentEmail}）`,
      );
      continue;
    }

    for (const [email, name] of [
      [recipientEmail, recipientName],
      [respondentEmail, respondentName],
    ]) {
      const knownName = nameByEmail.get(email);
      if (knownName === undefined) {
        nameByEmail.set(email, name);
      } else if (knownName !== name) {
        conflictingEmails.add(email);
      }
    }

    const key = JSON.stringify([recipientEmail, respondentEmail]);
    if (seen.has(key)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(key);

    rows.push({
      recipientEmail,
      recipientName,
      respondentEmail,
      respondentName,
    });
  }

  return {
    rows,
    errors,
    duplicateCount,
    conflictingNameCount: conflictingEmails.size,
  };
}

/** 入稿データに現れるメールアドレスを重複なく取り出す */
export function collectEmails(rows: StakeholderRelationInput[]): string[] {
  return Array.from(
    new Set(rows.flatMap((row) => [row.recipientEmail, row.respondentEmail])),
  );
}
